import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import {
  BaseQueryParams,
  IResponse,
  IResponsePaginate,
  Paa,
  PaaMilestone,
  WithMetadata,
  PaaDetails,
  AcYearAvailability,
  TaskHistory,
  PaaModeleRequest,
} from '@/types';

interface PaaQuery extends BaseQueryParams {
  ref?: string;
  nom?: string;
  annee?: string;
  budgetEstime?: number;
  statusId?: string;
}

interface MilestoneQuery extends BaseQueryParams {
  realisationEnvisagees?: string;
  serviceId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PaaService {
  BASEURL_PAA = `${environment.api.paaUrl}/paas`;
  BASEURL_REALISATION = `${environment.api.paaUrl}/realisations`;

  httpClient = inject(HttpClient);

  getPaas(params?: PaaQuery): Observable<IResponsePaginate<Paa>> {
    return this.httpClient.get<IResponsePaginate<Paa>>(`${this.BASEURL_PAA}`, { params: params as HttpParams });
  }

  getPaa(id: number): Observable<WithMetadata<Paa>> {
    return this.httpClient.get<WithMetadata<Paa>>(`${this.BASEURL_PAA}/${id}`);
  }

  getPaaVersion(id: number, version: number): Observable<WithMetadata<Paa>> {
    return this.httpClient.get<WithMetadata<Paa>>(`${this.BASEURL_PAA}/${id}/${version}`);
  }

  getPaaDetails(id: number): Observable<PaaDetails> {
    return this.httpClient.get<PaaDetails>(`${this.BASEURL_PAA}/details/${id}`);
  }

  // to review
  getCurrentPaa(): Observable<WithMetadata<Paa>> {
    return this.httpClient.get<WithMetadata<Paa>>(`${this.BASEURL_PAA}/current_year`).pipe(
      switchMap(response => {
        const id = response.id;
        return this.getPaa(id);
      })
    );
  }

  getCurrentUserPaa(params?: PaaQuery): Observable<IResponsePaginate<Paa>> {
    return this.httpClient.get<IResponsePaginate<Paa>>(`${this.BASEURL_PAA}/current_year/user`, { params: params as HttpParams });
  }

  getPaaHistory(id: number, params?: PaaQuery): Observable<IResponsePaginate<Paa>> {
    return this.httpClient.get<IResponsePaginate<Paa>>(`${this.BASEURL_PAA}/history/minimal/${id}`, { params: params as HttpParams });
  }

  getAcWithNoPaa(): Observable<AcYearAvailability[]> {
    return this.httpClient.get<AcYearAvailability[]>(`${this.BASEURL_PAA}/current_year/user/acs_without_paa`);
  }

  startWorkflow(id: number): Observable<IResponse<Paa>> {
    return this.httpClient.post<IResponse<Paa>>(`${this.BASEURL_PAA}/flowable/start/${id}`, {});
  }

  getPaaTaskHistory(processInstanceId: string): Observable<TaskHistory[]> {
    return this.httpClient.get<TaskHistory[]>(`${this.BASEURL_PAA}/flowable/task-history/${processInstanceId}`);
  }

  refreshPaa(paaId: number): Observable<IResponse<Paa>> {
    return this.httpClient.post<IResponse<Paa>>(`${this.BASEURL_PAA}/actualise/${paaId}`, {});
  }

  addPaa(paa: FormData): Observable<IResponse<Paa>> {
    return this.httpClient.post<IResponse<Paa>>(`${this.BASEURL_PAA}/with-relationships`, paa);
  }

  addPaaPerAc(paa: FormData): Observable<IResponse<Paa>> {
    return this.httpClient.post<IResponse<Paa>>(`${this.BASEURL_PAA}/create_per_ac`, paa);
  }

  updatePaa(id: number, version: number, paa: FormData): Observable<IResponse<Paa>> {
    return this.httpClient.put<IResponse<Paa>>(`${this.BASEURL_PAA}/${id}/${version}`, paa);
  }

  deletePaa(id: number, version = 1): Observable<IResponse<Paa>> {
    return this.httpClient.delete<IResponse<Paa>>(`${this.BASEURL_PAA}/${id}/${version}`);
  }

  // deprecated
  getCurrentPaaDetails(): Observable<PaaDetails> {
    return this.httpClient.get<WithMetadata<Paa>>(`${this.BASEURL_PAA}/current_year`).pipe(
      switchMap(response => {
        const id = response.id;
        return this.httpClient.get<PaaDetails>(`${this.BASEURL_PAA}/details/${id}`);
      })
    );
  }

  getRealisations(params?: MilestoneQuery): Observable<IResponsePaginate<PaaMilestone>> {
    return this.httpClient.get<IResponsePaginate<PaaMilestone>>(`${this.BASEURL_REALISATION}`, { params: params as HttpParams });
  }

  downloadPaaModel(modelRequest: PaaModeleRequest): Observable<Blob> {
    return this.httpClient.post(`${this.BASEURL_PAA}/flowable/modele-paa`, modelRequest, {
      responseType: 'blob',
    });
  }

  downloadExcelPaaModel(modelRequest: Partial<PaaModeleRequest>): Observable<Blob> {
    return this.httpClient.post(`${this.BASEURL_PAA}/flowable/modele-paa-excel`, modelRequest, {
      responseType: 'blob',
    });
  }

  uploadPaaModel(taskId: string, paaFormData: FormData): Observable<IResponse<PaaDetails>> {
    return this.httpClient.post<IResponse<PaaDetails>>(`${this.BASEURL_PAA}/flowable/tasks/${taskId}/validate_with_model`, paaFormData);
  }
}
